/**
 * Декораторы для упрощения создания миграций
 */

import { IMigration, MigrationResult, MigrationContext, MigrationType } from '../types/migration-interfaces';

/**
 * Реестр миграций
 */
export class MigrationRegistry {
  private static migrations: Map<string, IMigration> = new Map();
  
  static register(migration: IMigration): void {
    this.migrations.set(migration.id, migration);
  }
  
  static get(id: string): IMigration | undefined {
    return this.migrations.get(id);
  }
  
  static getAll(): IMigration[] {
    return Array.from(this.migrations.values());
  }
  
  static clear(): void {
    this.migrations.clear();
  }
}

/**
 * Декоратор для класса миграции
 */
export function Migration(config: {
  id: string;
  version: string;
  description: string;
  type?: MigrationType;
  dependencies?: string[];
}) {
  return function <T extends { new (...args: any[]): any }>(constructor: T) {
    const migration: IMigration = {
      id: config.id,
      version: config.version,
      description: config.description,
      type: config.type || 'custom',
      dependencies: config.dependencies || [],
      
      async up(context: MigrationContext): Promise<MigrationResult> {
        const instance = new constructor();
        if (typeof instance.up === 'function') {
          return await instance.up(context);
        }
        throw new Error(`Migration ${config.id} must implement up() method`);
      },
      
      async down(context: MigrationContext): Promise<MigrationResult> {
        const instance = new constructor();
        if (typeof instance.down === 'function') {
          return await instance.down(context);
        }
        throw new Error(`Migration ${config.id} must implement down() method`);
      },
      
      async canApply(context: MigrationContext): Promise<boolean> {
        const instance = new constructor();
        if (typeof instance.canApply === 'function') {
          return await instance.canApply(context);
        }
        return true;
      },
      
      async canRollback(context: MigrationContext): Promise<boolean> {
        const instance = new constructor();
        if (typeof instance.canRollback === 'function') {
          return await instance.canRollback(context);
        }
        return true;
      },
      
      async validate(context: MigrationContext): Promise<boolean> {
        const instance = new constructor();
        if (typeof instance.validate === 'function') {
          return await instance.validate(context);
        }
        return true;
      }
    };
    
    // Автоматически регистрируем миграцию
    MigrationRegistry.register(migration);
    
    return constructor;
  };
}

/**
 * Декоратор для методов миграции
 */
export function Up(_target: any, _propertyKey: string, descriptor: PropertyDescriptor) {
  // Метод уже определен в классе, декоратор просто помечает его
  return descriptor;
}

/**
 * Декоратор для методов отката
 */
export function Down(_target: any, _propertyKey: string, descriptor: PropertyDescriptor) {
  // Метод уже определен в классе, декоратор просто помечает его
  return descriptor;
}

/**
 * Декоратор для проверки возможности применения
 */
export function CanApply(_target: any, _propertyKey: string, descriptor: PropertyDescriptor) {
  return descriptor;
}

/**
 * Декоратор для проверки возможности отката
 */
export function CanRollback(_target: any, _propertyKey: string, descriptor: PropertyDescriptor) {
  return descriptor;
}

/**
 * Декоратор для валидации
 */
export function Validate(_target: any, _propertyKey: string, descriptor: PropertyDescriptor) {
  return descriptor;
}

/**
 * Декоратор для зависимостей
 */
export function DependsOn(...migrationIds: string[]) {
  return function (target: any, _propertyKey?: string) {
    if (!target.dependencies) {
      target.dependencies = [];
    }
    target.dependencies.push(...migrationIds);
  };
}

/**
 * Декоратор для типа миграции
 */
export function Type(type: MigrationType) {
  return function (target: any, _propertyKey?: string) {
    target.migrationType = type;
  };
}

/**
 * Базовый класс для миграций
 */
export abstract class BaseMigration {
  abstract up(context: MigrationContext): Promise<MigrationResult>;
  abstract down(context: MigrationContext): Promise<MigrationResult>;
  
  async canApply(_context: MigrationContext): Promise<boolean> {
    return true;
  }
  
  async canRollback(_context: MigrationContext): Promise<boolean> {
    return true;
  }
  
  async validate(_context: MigrationContext): Promise<boolean> {
    return true;
  }
  
  protected createResult(success: boolean, options: {
    error?: string;
    message?: string;
    processedCount?: number;
    executionTime?: number;
    metadata?: Record<string, any>;
  } = {}): MigrationResult {
    return {
      success,
      ...options
    };
  }
  
  protected async measureTime<T>(operation: () => Promise<T>): Promise<{ result: T; executionTime: number }> {
    const startTime = Date.now();
    const result = await operation();
    const executionTime = Date.now() - startTime;
    return { result, executionTime };
  }
}

/**
 * Миграция схемы
 */
export abstract class SchemaMigration extends BaseMigration {
  protected abstract schemaName: string;
  protected abstract oldVersion: string;
  protected abstract newVersion: string;
  
  async up(context: MigrationContext): Promise<MigrationResult> {
    const { executionTime } = await this.measureTime(async () => {
      return this.updateSchema(context);
    });
    
    return this.createResult(true, {
      message: `Schema ${this.schemaName} updated from ${this.oldVersion} to ${this.newVersion}`,
      processedCount: 1,
      executionTime
    });
  }
  
  async down(context: MigrationContext): Promise<MigrationResult> {
    const { executionTime } = await this.measureTime(async () => {
      return this.rollbackSchema(context);
    });
    
    return this.createResult(true, {
      message: `Schema ${this.schemaName} rolled back from ${this.newVersion} to ${this.oldVersion}`,
      processedCount: 1,
      executionTime
    });
  }
  
  protected abstract updateSchema(context: MigrationContext): Promise<void>;
  protected abstract rollbackSchema(context: MigrationContext): Promise<void>;
}

/**
 * Миграция данных
 */
export abstract class DataMigration extends BaseMigration {
  protected abstract elementType?: string;
  protected batchSize: number = 100;
  
  async up(context: MigrationContext): Promise<MigrationResult> {
    const { result, executionTime } = await this.measureTime(async () => {
      return this.transformData(context);
    });
    
    return this.createResult(true, {
      message: `Transformed ${result.processedCount} elements`,
      processedCount: result.processedCount,
      executionTime
    });
  }
  
  async down(context: MigrationContext): Promise<MigrationResult> {
    const { result, executionTime } = await this.measureTime(async () => {
      return this.rollbackData(context);
    });
    
    return this.createResult(true, {
      message: `Rolled back ${result.processedCount} elements`,
      processedCount: result.processedCount,
      executionTime
    });
  }
  
  protected abstract transformData(context: MigrationContext): Promise<{ processedCount: number }>;
  protected abstract rollbackData(context: MigrationContext): Promise<{ processedCount: number }>;
}

/**
 * Утилиты для создания миграций
 */
export class MigrationUtils {
  /**
   * Создает простую миграцию
   */
  static simple(config: {
    id: string;
    version: string;
    description: string;
    type?: MigrationType;
    dependencies?: string[];
    up: (context: MigrationContext) => Promise<MigrationResult>;
    down: (context: MigrationContext) => Promise<MigrationResult>;
    canApply?: (context: MigrationContext) => Promise<boolean>;
    canRollback?: (context: MigrationContext) => Promise<boolean>;
    validate?: (context: MigrationContext) => Promise<boolean>;
  }): IMigration {
    return {
      id: config.id,
      version: config.version,
      description: config.description,
      type: config.type || 'custom',
      dependencies: config.dependencies || [],
      up: config.up,
      down: config.down,
      canApply: config.canApply || (async () => true),
      canRollback: config.canRollback || (async () => true),
      validate: config.validate || (async () => true)
    };
  }
  
  /**
   * Создает миграцию только для применения (без отката)
   */
  static upOnly(config: {
    id: string;
    version: string;
    description: string;
    type?: MigrationType;
    dependencies?: string[];
    up: (context: MigrationContext) => Promise<MigrationResult>;
  }): IMigration {
    return this.simple({
      ...config,
      down: async () => ({
        success: false,
        error: 'This migration cannot be rolled back'
      }),
      canRollback: async () => false
    });
  }
  
  /**
   * Создает миграцию-заглушку
   */
  static noop(config: {
    id: string;
    version: string;
    description: string;
    type?: MigrationType;
    dependencies?: string[];
  }): IMigration {
    return this.simple({
      ...config,
      up: async () => ({
        success: true,
        message: 'No-op migration executed successfully',
        processedCount: 0,
        executionTime: 0
      }),
      down: async () => ({
        success: true,
        message: 'No-op migration rolled back successfully',
        processedCount: 0,
        executionTime: 0
      })
    });
  }
} 